import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  Clock,
  Copy,
  Landmark,
  MoreHorizontal,
  Printer,
  RefreshCw,
  XCircle,
} from "lucide-react";
import { Badge, Button } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { useAuth } from "@/hooks/useAuth";
import {
  useArchiveDeal,
  useCloneDeal,
  useRestoreDeal,
} from "@/hooks/deals/useDeals";
import { useDocumentRequests } from "@/hooks/deals/useDealDetail";
import { DealAssignmentPills } from "@/components/DealFlow/DealAssignmentPills";
import { ageBadgeStatus, ageSeverity, dealBusinessLabel, daysSince, formatCurrency } from "./dealFormat";
import type { DealListItemDto } from "@/types/deals";
import { getPrimaryAction, type PrimaryActionId } from "./getPrimaryAction";
import { DeclineModal } from "./DeclineModal";
import { WithdrawModal } from "./WithdrawModal";
import { ReassignModal } from "./ReassignModal";
import { DeclineProposalModal } from "./DeclineProposalModal";
import { canProposeDecline } from "./declineProposalRules";
import { canArchiveOrRestoreDeal, canWriteToDeal, isTerminalStage } from "./dealMenuRules";
import { DeclineConfirmModal } from "./DeclineConfirmModal";
import { DeclineWithdrawModal } from "./DeclineWithdrawModal";
import { CreateEscrowModal } from "@/components/escrow/CreateEscrowModal";
import { useDeclineState } from "@/hooks/deals/useDeclineWorkflow";
import { DEAL_FLOW_PERMISSIONS } from "@/lib/dealFlowPermissions";
import { ApiError } from "@/api/client";
import {
  DEAL_PERMISSIONS,
  STAGE_LABEL,
  subStateLabelFor,
  type DealDetailDto,
} from "@/types/deals";

interface Props {
  deal: DealDetailDto;
  /** Triggered for primary actions that the page handles inline (e.g.
   *  switching tabs, opening modals owned by the page). */
  onPrimaryAction: (id: PrimaryActionId) => void;
}

/**
 * Spec §5.5 — sticky deal-detail header. Renders deal identity, stage +
 * sub-state badges, days-in-stage SLA pill, the 7 assignment pills, the
 * contextual primary action, and the overflow menu.
 */
export function DealDetailHeader({ deal, onPrimaryAction }: Props) {
  const { user, hasPermission, hasRoleCode } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  const [declineOpen, setDeclineOpen] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [reassignRole, setReassignRole] = useState<"C1" | "B1" | "A1" | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [escrowOpen, setEscrowOpen] = useState(false);

  // Escrow.com Integration — gate the "Create Escrow Transaction" entry on the
  // Escrow.Draft permission; disable it (with a tooltip) until the deal has a
  // resolved borrower. Buyer-email presence is validated server-side on draft.
  // The draft is refused on a Closed, Declined or Withdrawn deal
  // (EscrowTransactionService.DraftAsync), so the item is hidden there.
  const isTerminal = isTerminalStage(deal.stage);
  const canDraftEscrow = hasPermission("Escrow.Draft") && !isTerminal;
  const borrowerResolved = Boolean(deal.borrowerPartyId);

  // Deal-level decline workflow modal state.
  const [proposeDeclineOpen, setProposeDeclineOpen] = useState(false);
  const [confirmDeclineOpen, setConfirmDeclineOpen] = useState(false);
  const [withdrawDeclineOpen, setWithdrawDeclineOpen] = useState(false);

  const declineStatus = deal.declineStatus ?? "Active";
  const declineStateQuery = useDeclineState(
    declineStatus === "DeclineProposed" ? deal.id : undefined,
  );
  const canPropose = canProposeDecline({
    stage: deal.stage,
    declineStatus,
    hasProposePermission: hasPermission(DEAL_FLOW_PERMISSIONS.ProposeDecline),
  });
  const canConfirm =
    declineStatus === "DeclineProposed" &&
    hasPermission(DEAL_FLOW_PERMISSIONS.ConfirmDecline);
  const canWithdraw =
    declineStatus === "DeclineProposed" &&
    hasPermission(DEAL_FLOW_PERMISSIONS.WithdrawDeclineProposal);
  // Reassign, the pills, Archive, Restore and Mark withdrawn write under
  // /api/deals/{id}/, which the read-only middlewares refuse. Archive and
  // Restore stay open on a Withdrawn or stage-Declined deal, and on a ratified
  // decline to ADMIN only (owner ruling, 2026-10-26).
  const lockOptions = {
    stage: deal.stage,
    declineStatus,
    canReopenClosedDeal: hasPermission(DEAL_FLOW_PERMISSIONS.ReopenClosedDeal),
    canEditWithdrawnOrDeclinedDeal: hasPermission(
      DEAL_FLOW_PERMISSIONS.EditWithdrawnOrDeclinedDeal,
    ),
  };
  const canWrite = canWriteToDeal(lockOptions);
  const canArchiveOrRestore = canArchiveOrRestoreDeal(lockOptions);

  const archive = useArchiveDeal(deal.id);
  const restore = useRestoreDeal(deal.id);
  const clone = useCloneDeal(deal.id);

  // getPrimaryAction takes the document requests but no longer reads them.
  // Skip the request fetch outside the Pre-Approval stage to avoid an extra
  // round-trip for every header.
  const docRequestsQuery = useDocumentRequests(
    deal.stage === "PreApproval" ? deal.id : undefined,
  );
  const primary = useMemo(
    () =>
      getPrimaryAction(
        deal,
        { hasPermission, userId: user?.id },
        docRequestsQuery.data ?? [],
      ),
    [deal, hasPermission, user?.id, docRequestsQuery.data],
  );

  const ageDays = daysSince(deal.stageEnteredDate);
  // DealDetailDto carries every field DealListItemDto expects; the helpers
  // were authored against the list shape but read-only access is safe.
  const listView = deal as unknown as DealListItemDto;
  const severity = ageSeverity(listView);
  const subStateLabel = subStateLabelFor(deal.stage, deal.subState);

  // Mirrors the reassign endpoints: the Deals.Reassign policy plus the C0, E1
  // or ADMIN role (DealAssignmentService.ReassignAsync). Reassign is the only
  // way to change an assigned C1, B1 or A1 (owner rulings, 2026-10-09).
  const canReassign =
    canWrite &&
    hasPermission(DEAL_PERMISSIONS.Reassign) &&
    (hasRoleCode("C0") || hasRoleCode("E1") || hasRoleCode("ADMIN"));

  async function onArchive() {
    try {
      await archive.mutateAsync({});
      toast.success("Deal archived.");
      setMenuOpen(false);
    } catch (err) {
      toast.error(
        err instanceof ApiError && err.errors.length
          ? err.errors.join("; ")
          : "Archive failed.",
      );
    }
  }

  async function onRestore() {
    try {
      await restore.mutateAsync();
      toast.success("Deal restored.");
      setMenuOpen(false);
    } catch (err) {
      toast.error(
        err instanceof ApiError && err.errors.length
          ? err.errors.join("; ")
          : "Restore failed.",
      );
    }
  }

  async function onClone() {
    try {
      const cloned = await clone.mutateAsync({});
      toast.success(`Cloned to ${cloned.dealNumber}.`);
      setMenuOpen(false);
      navigate(`/pipeline/${cloned.id}`);
    } catch (err) {
      toast.error(
        err instanceof ApiError && err.errors.length
          ? err.errors.join("; ")
          : "Clone failed.",
      );
    }
  }

  return (
    <header className="flex flex-col gap-space-3 mb-space-4 pb-space-4 border-b border-border-light">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-space-4">
        <div className="min-w-0 flex-1">
          <div className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow mb-space-1">
            {deal.dealNumber}
          </div>
          <h1 className="font-display font-extrabold text-h2 uppercase tracking-wider text-onyx m-0 break-words">
            {dealBusinessLabel(listView)}
          </h1>
          <div className="flex flex-wrap items-center gap-space-2 mt-space-2">
            <Badge status="draft">{STAGE_LABEL[deal.stage]}</Badge>
            {subStateLabel && (
              <>
                <span className="text-caption text-gray-mid">→</span>
                <Badge status="info">{subStateLabel}</Badge>
              </>
            )}
            <span className="inline-flex items-center gap-space-1 text-caption text-gray-mid ml-space-2">
              <Clock size={12} aria-hidden />
              <Badge status={ageBadgeStatus(severity)}>
                {ageDays} day{ageDays === 1 ? "" : "s"} in {subStateLabel ?? STAGE_LABEL[deal.stage]}
              </Badge>
            </span>
            {deal.isArchived && <Badge status="archived">Archived</Badge>}
            <span className="text-small text-gray-mid">
              · {formatCurrency(deal.purchasePrice)}
            </span>
            <span className="text-small text-gray-mid">
              Broker Liaison: {deal.assignedC1Id ? deal.assignedC1DisplayName?.trim() || "Needs assignment" : "Unassigned"}
            </span>
            {deal.stagingSubmissionId && (
              <Link
                to={`/deal-flow/deal-staging/${deal.stagingSubmissionId}`}
                className="text-small text-gold-shadow hover:underline"
              >
                View staging
              </Link>
            )}
          </div>
        </div>

        <div className="flex items-start gap-space-3 shrink-0">
          {/* 7-pill assignment widget replaces
              the legacy 3-icon AssigneeStack. The C1, B1 and A1 pills
              follow the header, so the overflow menu's Reassign items are
              the only way to change them; the pill widget assigns
              the other four. */}
          <DealAssignmentPills dealId={deal.id} dealStage={deal.stage} readOnly={!canWrite} />

          <div className="flex items-center gap-space-2 relative">
            {primary && (
              <Button
                variant="primary"
                onClick={() => onPrimaryAction(primary.id)}
                disabled={primary.disabled}
                title={primary.disabledReason ?? primary.tooltip}
              >
                {primary.label}
              </Button>
            )}

            <button
              type="button"
              className="inline-flex items-center justify-center h-10 w-10 rounded-md border border-border-light hover:bg-white-warm"
              onClick={() => setMenuOpen((o) => !o)}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              aria-label="More actions"
            >
              <MoreHorizontal size={16} aria-hidden />
            </button>

            {menuOpen && (
              <>
                <div
                  className="fixed inset-0 z-30"
                  onClick={() => setMenuOpen(false)}
                  aria-hidden
                />
                <ul
                  className="absolute right-0 top-12 z-40 min-w-[220px] bg-white-pure border border-border-light rounded-md shadow-md py-space-1 m-0 list-none"
                  role="menu"
                >
                  {!deal.isArchived && !isTerminal && hasPermission(DEAL_PERMISSIONS.Decline) && (
                    <MenuItem
                      icon={<XCircle size={14} aria-hidden />}
                      onClick={() => {
                        setDeclineOpen(true);
                        setMenuOpen(false);
                      }}
                    >
                      Decline deal
                    </MenuItem>
                  )}
                  {!deal.isArchived && !isTerminal && canWrite && hasPermission(DEAL_PERMISSIONS.Withdraw) && (
                    <MenuItem
                      icon={<XCircle size={14} aria-hidden />}
                      onClick={() => {
                        setWithdrawOpen(true);
                        setMenuOpen(false);
                      }}
                    >
                      Mark withdrawn
                    </MenuItem>
                  )}
                  {/* Deal-level decline workflow. */}
                  {canPropose && (
                    <MenuItem
                      icon={<XCircle size={14} aria-hidden />}
                      onClick={() => {
                        setProposeDeclineOpen(true);
                        setMenuOpen(false);
                      }}
                    >
                      Propose Decline
                    </MenuItem>
                  )}
                  {canConfirm && (
                    <MenuItem
                      icon={<XCircle size={14} aria-hidden />}
                      onClick={() => {
                        setConfirmDeclineOpen(true);
                        setMenuOpen(false);
                      }}
                    >
                      Confirm Decline
                    </MenuItem>
                  )}
                  {canWithdraw && (
                    <MenuItem
                      icon={<RefreshCw size={14} aria-hidden />}
                      onClick={() => {
                        setWithdrawDeclineOpen(true);
                        setMenuOpen(false);
                      }}
                    >
                      Withdraw Decline Proposal
                    </MenuItem>
                  )}
                  {/* Shown even with no C1: the C1 pill follows the header
                      (owner ruling, 2026-10-09), so this is the only way to set
                      one. reassign-c1 accepts an empty previous C1. */}
                  {canReassign && (
                    <MenuItem
                      icon={<RefreshCw size={14} aria-hidden />}
                      onClick={() => {
                        setReassignRole("C1");
                        setMenuOpen(false);
                      }}
                    >
                      {deal.assignedC1Id ? "Reassign C1" : "Assign C1"}
                    </MenuItem>
                  )}
                  {canReassign && deal.assignedB1Id && (
                    <MenuItem
                      icon={<RefreshCw size={14} aria-hidden />}
                      onClick={() => {
                        setReassignRole("B1");
                        setMenuOpen(false);
                      }}
                    >
                      Reassign B1
                    </MenuItem>
                  )}
                  {canReassign && deal.assignedA1Id && (
                    <MenuItem
                      icon={<RefreshCw size={14} aria-hidden />}
                      onClick={() => {
                        setReassignRole("A1");
                        setMenuOpen(false);
                      }}
                    >
                      Reassign A1
                    </MenuItem>
                  )}
                  {hasPermission(DEAL_PERMISSIONS.Create) &&
                    (deal.stage === "Declined" || deal.stage === "Withdrawn") && (
                      <MenuItem
                        icon={<Copy size={14} aria-hidden />}
                        onClick={onClone}
                      >
                        Clone deal
                      </MenuItem>
                    )}
                  {!deal.isArchived && canArchiveOrRestore && hasPermission(DEAL_PERMISSIONS.Archive) && (
                    <MenuItem
                      icon={<AlertTriangle size={14} aria-hidden />}
                      onClick={onArchive}
                    >
                      Archive deal
                    </MenuItem>
                  )}
                  {deal.isArchived && canArchiveOrRestore && hasPermission(DEAL_PERMISSIONS.Restore) && (
                    <MenuItem
                      icon={<RefreshCw size={14} aria-hidden />}
                      onClick={onRestore}
                    >
                      Restore deal
                    </MenuItem>
                  )}
                  {canDraftEscrow && (
                    <MenuItem
                      icon={<Landmark size={14} aria-hidden />}
                      disabled={!borrowerResolved}
                      title={
                        borrowerResolved
                          ? undefined
                          : "Set the deal's buyer (borrower) with an email before creating an escrow transaction."
                      }
                      onClick={() => {
                        setEscrowOpen(true);
                        setMenuOpen(false);
                      }}
                    >
                      Create Escrow Transaction
                    </MenuItem>
                  )}
                  <MenuItem
                    icon={<Printer size={14} aria-hidden />}
                    onClick={() => {
                      window.print();
                      setMenuOpen(false);
                    }}
                  >
                    Print summary
                  </MenuItem>
                </ul>
              </>
            )}
          </div>
        </div>
      </div>

      <DeclineModal
        open={declineOpen}
        deal={declineOpen ? deal : null}
        onClose={() => setDeclineOpen(false)}
      />
      <WithdrawModal
        open={withdrawOpen}
        deal={withdrawOpen ? deal : null}
        onClose={() => setWithdrawOpen(false)}
      />
      {reassignRole && (
        <ReassignModal
          open={!!reassignRole}
          deal={deal}
          role={reassignRole}
          onClose={() => setReassignRole(null)}
        />
      )}

      {/* Decline workflow modals. */}
      <DeclineProposalModal
        open={proposeDeclineOpen}
        dealId={deal.id}
        dealNumber={deal.dealNumber}
        onClose={() => setProposeDeclineOpen(false)}
      />
      <DeclineConfirmModal
        open={confirmDeclineOpen}
        dealId={deal.id}
        dealNumber={deal.dealNumber}
        proposal={declineStateQuery.data ?? null}
        onClose={() => setConfirmDeclineOpen(false)}
      />
      <DeclineWithdrawModal
        open={withdrawDeclineOpen}
        dealId={deal.id}
        dealNumber={deal.dealNumber}
        onClose={() => setWithdrawDeclineOpen(false)}
      />
      <CreateEscrowModal
        open={escrowOpen}
        deal={escrowOpen ? deal : null}
        onClose={() => setEscrowOpen(false)}
      />
    </header>
  );
}

function MenuItem({
  icon,
  children,
  onClick,
  disabled = false,
  title,
}: {
  icon?: React.ReactNode;
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  title?: string;
}) {
  return (
    <li role="none">
      <button
        type="button"
        role="menuitem"
        onClick={onClick}
        disabled={disabled}
        title={title}
        className="w-full text-left flex items-center gap-space-2 px-space-3 py-space-2 text-small text-onyx hover:bg-white-warm disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
      >
        {icon}
        <span>{children}</span>
      </button>
    </li>
  );
}

